import React, { useState, useEffect } from 'react';
import { Shield, Lock, CheckCircle, Fingerprint, Key, Globe, Zap, Users, ArrowRight, Menu, X, Database, Eye, Server } from 'lucide-react';

function App() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % 4);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const features = [
    {
      icon: <Shield style={{width: '48px', height: '48px'}} />,
      title: "Decentralized Security",
      description: "Your identity stored on blockchain, protected by cryptographic algorithms and distributed across nodes."
    },
    {
      icon: <Fingerprint style={{width: '48px', height: '48px'}} />,
      title: "Biometric Integration",
      description: "Multi-factor authentication with biometric verification for enhanced security and seamless access."
    },
    {
      icon: <Key style={{width: '48px', height: '48px'}} />,
      title: "Self-Sovereign Identity",
      description: "You control your data. Share what you want, when you want, with complete ownership."
    },
    {
      icon: <Zap style={{width: '48px', height: '48px'}} />,
      title: "Instant Verification",
      description: "Lightning-fast identity verification powered by blockchain consensus mechanisms."
    }
  ];

  const stats = [
    { value: "99.9%", label: "Uptime" },
    { value: "500K+", label: "Verified Users" },
    { value: "<0.5s", label: "Verification Time" },
    { value: "256-bit", label: "Encryption" }
  ];

  const benefits = [
    {
      icon: <Database style={{width: '32px', height: '32px'}} />,
      title: "Immutable Records",
      description: "Once verified, your identity credentials are permanently and securely stored on the blockchain."
    },
    {
      icon: <Eye style={{width: '32px', height: '32px'}} />,
      title: "Privacy First",
      description: "Zero-knowledge proofs allow verification without exposing your personal information."
    },
    {
      icon: <Server style={{width: '32px', height: '32px'}} />,
      title: "No Single Point of Failure",
      description: "Distributed architecture ensures your identity is always accessible and secure."
    }
  ];

  return (
    <div style={{minHeight: '100vh', background: 'linear-gradient(to bottom right, #1e1b4b, #1e3a8a, #0f172a)', color: 'white', fontFamily: 'system-ui, -apple-system, sans-serif'}}>
      
      {/* Navigation */}
      <nav style={{
        position: 'fixed',
        width: '100%',
        zIndex: 50,
        transition: 'all 0.3s',
        backgroundColor: scrolled ? 'rgba(49, 46, 129, 0.95)' : 'transparent',
        backdropFilter: scrolled ? 'blur(12px)' : 'none',
        boxShadow: scrolled ? '0 10px 15px -3px rgba(0, 0, 0, 0.1)' : 'none'
      }}>
        <div style={{maxWidth: '1280px', margin: '0 auto', padding: '0 1rem'}}>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '80px'}}>
            
            {/* Logo */}
            <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
              <div style={{position: 'relative'}}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  background: 'linear-gradient(to bottom right, #3b82f6, #4f46e5)',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                  transform: 'rotate(45deg)'
                }}>
                  <Shield style={{width: '28px', height: '28px', color: 'white', transform: 'rotate(-45deg)'}} />
                </div>
                <div style={{
                  position: 'absolute',
                  top: '-4px',
                  right: '-4px',
                  width: '16px',
                  height: '16px',
                  backgroundColor: '#34d399',
                  borderRadius: '50%',
                  border: '2px solid #312e81',
                  animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                }}></div>
              </div>
              <span style={{fontSize: '1.5rem', fontWeight: 'bold', letterSpacing: '-0.025em'}}>
                ID<span style={{color: '#60a5fa'}}>sec</span><span style={{color: '#a5b4fc'}}>.live</span>
              </span>
            </div>

            {/* Desktop Menu */}
            <div style={{display: window.innerWidth >= 768 ? 'flex' : 'none', alignItems: 'center', gap: '2rem'}}>
              <a href="#features" style={{color: 'white', textDecoration: 'none'}}>Features</a>
              <a href="#security" style={{color: 'white', textDecoration: 'none'}}>Security</a>
              <a href="#technology" style={{color: 'white', textDecoration: 'none'}}>Technology</a>
              <a href="#contact" style={{color: 'white', textDecoration: 'none'}}>Contact</a>
              <button style={{
                background: 'linear-gradient(to right, #3b82f6, #4f46e5)',
                padding: '0.5rem 1.5rem',
                borderRadius: '8px',
                fontWeight: '600',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
              }}>
                Get Started
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              style={{
                display: window.innerWidth < 768 ? 'block' : 'none',
                padding: '0.5rem',
                background: 'none',
                border: 'none',
                color: 'white',
                cursor: 'pointer'
              }}
            >
              {mobileMenuOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div style={{backgroundColor: '#312e81', borderTop: '1px solid #4c1d95'}}>
            <div style={{padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem'}}>
              <a href="#features" style={{display: 'block', padding: '0.5rem 0', color: 'white', textDecoration: 'none'}}>Features</a>
              <a href="#security" style={{display: 'block', padding: '0.5rem 0', color: 'white', textDecoration: 'none'}}>Security</a>
              <a href="#technology" style={{display: 'block', padding: '0.5rem 0', color: 'white', textDecoration: 'none'}}>Technology</a>
              <a href="#contact" style={{display: 'block', padding: '0.5rem 0', color: 'white', textDecoration: 'none'}}>Contact</a>
              <button style={{
                width: '100%',
                background: 'linear-gradient(to right, #3b82f6, #4f46e5)',
                padding: '0.5rem 1.5rem',
                borderRadius: '8px',
                fontWeight: '600',
                border: 'none',
                color: 'white',
                cursor: 'pointer'
              }}>
                Get Started
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <div style={{position: 'relative', paddingTop: '8rem', paddingBottom: '5rem', padding: '8rem 1rem 5rem', overflow: 'hidden'}}>
        <div style={{position: 'absolute', inset: 0, opacity: 0.2, pointerEvents: 'none'}}>
          <div style={{
            position: 'absolute',
            top: '80px',
            left: '40px',
            width: '288px',
            height: '288px',
            backgroundColor: '#3b82f6',
            borderRadius: '50%',
            filter: 'blur(96px)',
            animation: 'pulse 3s ease-in-out infinite'
          }}></div>
          <div style={{
            position: 'absolute',
            bottom: '80px',
            right: '40px',
            width: '384px',
            height: '384px',
            backgroundColor: '#4f46e5',
            borderRadius: '50%',
            filter: 'blur(96px)',
            animation: 'pulse 3s ease-in-out infinite',
            animationDelay: '0.7s'
          }}></div>
        </div>

        <div style={{maxWidth: '1280px', margin: '0 auto', position: 'relative', zIndex: 10}}>
          <div style={{textAlign: 'center', maxWidth: '896px', margin: '0 auto'}}>
            <div style={{display: 'inline-block', marginBottom: '1.5rem'}}>
              <span style={{
                background: 'rgba(99, 102, 241, 0.3)',
                color: '#bfdbfe',
                padding: '0.5rem 1rem',
                borderRadius: '9999px',
                fontSize: '0.875rem',
                fontWeight: '600',
                backdropFilter: 'blur(4px)',
                border: '1px solid rgba(96, 165, 250, 0.3)'
              }}>
                Blockchain-Powered Identity Solutions
              </span>
            </div>
            
            <h1 style={{fontSize: 'clamp(2.5rem, 5vw, 4.5rem)', fontWeight: 'bold', marginBottom: '1.5rem', lineHeight: 1.2}}>
              Secure Your Digital
              <span style={{
                display: 'block',
                background: 'linear-gradient(to right, #93c5fd, #a5b4fc, #bfdbfe)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                Identity on Blockchain
              </span>
            </h1>
            
            <p style={{fontSize: 'clamp(1.125rem, 2vw, 1.5rem)', color: '#bfdbfe', marginBottom: '2.5rem', lineHeight: 1.75}}>
              Decentralized, immutable, and fully in your control. Experience the future of identity management with military-grade encryption and blockchain technology.
            </p>

            <div style={{display: 'flex', flexDirection: 'column', gap: '1rem', justifyContent: 'center', alignItems: 'center'}}>
              <button style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: 'linear-gradient(to right, #3b82f6, #4f46e5)',
                padding: '1rem 2rem',
                borderRadius: '8px',
                fontWeight: 'bold',
                fontSize: '1.125rem',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                border: 'none',
                color: 'white',
                cursor: 'pointer'
              }}>
                Learn More
                <ArrowRight style={{width: '1.25rem', height: '1.25rem'}} />
              </button>
              <button style={{
                border: '2px solid #a5b4fc',
                background: 'transparent',
                padding: '1rem 2rem',
                borderRadius: '8px',
                fontWeight: 'bold',
                fontSize: '1.125rem',
                color: 'white',
                cursor: 'pointer',
                backdropFilter: 'blur(4px)'
              }}>
                Explore Features
              </button>
            </div>
          </div>

          {/* Stats */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '1.5rem',
            marginTop: '5rem',
            maxWidth: '896px',
            margin: '5rem auto 0'
          }}>
            {stats.map((stat, idx) => (
              <div key={idx} style={{
                background: 'rgba(67, 56, 202, 0.3)',
                backdropFilter: 'blur(4px)',
                border: '1px solid rgba(79, 70, 229, 0.3)',
                borderRadius: '12px',
                padding: '1.5rem',
                textAlign: 'center'
              }}>
                <div style={{fontSize: 'clamp(1.875rem, 4vw, 2.25rem)', fontWeight: 'bold', color: '#93c5fd', marginBottom: '0.5rem'}}>{stat.value}</div>
                <div style={{color: '#bfdbfe', fontSize: '0.875rem'}}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div id="features" style={{padding: '5rem 1rem', background: 'rgba(2, 6, 23, 0.5)'}}>
        <div style={{maxWidth: '1280px', margin: '0 auto'}}>
          <div style={{textAlign: 'center', marginBottom: '4rem'}}>
            <h2 style={{fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 'bold', marginBottom: '1rem'}}>Powerful Features</h2>
            <p style={{fontSize: '1.25rem', color: '#bfdbfe'}}>Built for security, designed for simplicity</p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '2rem'
          }}>
            {features.map((feature, idx) => (
              <div 
                key={idx}
                style={{
                  background: activeFeature === idx 
                    ? 'linear-gradient(to bottom right, rgba(67, 56, 202, 0.6), rgba(15, 23, 42, 0.6))'
                    : 'linear-gradient(to bottom right, rgba(67, 56, 202, 0.4), rgba(15, 23, 42, 0.4))',
                  backdropFilter: 'blur(4px)',
                  border: activeFeature === idx ? '1px solid #60a5fa' : '1px solid rgba(79, 70, 229, 0.3)',
                  borderRadius: '16px',
                  padding: '2rem',
                  transition: 'all 0.3s',
                  cursor: 'pointer',
                  transform: activeFeature === idx ? 'scale(1.05)' : 'scale(1)',
                  boxShadow: activeFeature === idx ? '0 25px 50px -12px rgba(59, 130, 246, 0.3)' : 'none'
                }}
                onMouseEnter={() => setActiveFeature(idx)}
              >
                <div style={{
                  color: '#60a5fa',
                  marginBottom: '1rem',
                  transform: activeFeature === idx ? 'scale(1.1)' : 'scale(1)',
                  transition: 'transform 0.3s'
                }}>
                  {feature.icon}
                </div>
                <h3 style={{fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '0.75rem'}}>{feature.title}</h3>
                <p style={{color: '#bfdbfe', lineHeight: 1.75}}>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Security Section */}
      <div id="security" style={{padding: '5rem 1rem'}}>
        <div style={{maxWidth: '1280px', margin: '0 auto'}}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: window.innerWidth >= 768 ? '1fr 1fr' : '1fr',
            gap: '3rem',
            alignItems: 'center'
          }}>
            <div>
              <h2 style={{fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 'bold', marginBottom: '1.5rem'}}>
                Military-Grade Security
              </h2>
              <p style={{fontSize: '1.25rem', color: '#bfdbfe', marginBottom: '2rem', lineHeight: 1.75}}>
                Your identity is protected by the same blockchain technology that secures billions in digital assets. Multi-layered encryption ensures your data remains private and tamper-proof.
              </p>
              
              <div style={{display: 'flex', flexDirection: 'column', gap: '1rem'}}>
                {[
                  "End-to-end encryption",
                  "Distributed ledger technology",
                  "Zero-knowledge proofs",
                  "Multi-signature authentication",
                  "Cryptographic hashing",
                  "Immutable audit trails"
                ].map((item, idx) => (
                  <div key={idx} style={{display: 'flex', alignItems: 'center', gap: '0.75rem'}}>
                    <CheckCircle style={{width: '24px', height: '24px', color: '#34d399', flexShrink: 0}} />
                    <span style={{fontSize: '1.125rem'}}>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div style={{
                background: 'linear-gradient(to bottom right, rgba(67, 56, 202, 0.6), rgba(15, 23, 42, 0.6))',
                backdropFilter: 'blur(4px)',
                border: '1px solid rgba(79, 70, 229, 0.3)',
                borderRadius: '16px',
                padding: '2rem'
              }}>
                <div style={{display: 'flex', flexDirection: 'column', gap: '1.5rem'}}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    background: 'rgba(49, 46, 129, 0.5)',
                    padding: '1rem',
                    borderRadius: '8px',
                    border: '1px solid rgba(79, 70, 229, 0.3)'
                  }}>
                    <Lock style={{width: '40px', height: '40px', color: '#60a5fa'}} />
                    <div>
                      <div style={{fontWeight: '600'}}>AES-256 Encryption</div>
                      <div style={{fontSize: '0.875rem', color: '#93c5fd'}}>Bank-level security standard</div>
                    </div>
                  </div>
                  
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    background: 'rgba(49, 46, 129, 0.5)',
                    padding: '1rem',
                    borderRadius: '8px',
                    border: '1px solid rgba(79, 70, 229, 0.3)'
                  }}>
                    <Globe style={{width: '40px', height: '40px', color: '#60a5fa'}} />
                    <div>
                      <div style={{fontWeight: '600'}}>Distributed Network</div>
                      <div style={{fontSize: '0.875rem', color: '#93c5fd'}}>Global node infrastructure</div>
                    </div>
                  </div>
                  
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    background: 'rgba(49, 46, 129, 0.5)',
                    padding: '1rem',
                    borderRadius: '8px',
                    border: '1px solid rgba(79, 70, 229, 0.3)'
                  }}>
                    <Users style={{width: '40px', height: '40px', color: '#60a5fa'}} />
                    <div>
                      <div style={{fontWeight: '600'}}>Consensus Protocol</div>
                      <div style={{fontSize: '0.875rem', color: '#93c5fd'}}>Multi-party verification</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Technology Section */}
      <div id="technology" style={{padding: '5rem 1rem', background: 'rgba(2, 6, 23, 0.5)'}}>
        <div style={{maxWidth: '1280px', margin: '0 auto'}}>
          <div style={{textAlign: 'center', marginBottom: '4rem'}}>
            <h2 style={{fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 'bold', marginBottom: '1rem'}}>Advanced Technology</h2>
            <p style={{fontSize: '1.25rem', color: '#bfdbfe'}}>Built on cutting-edge blockchain infrastructure</p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '2rem'
          }}>
            {benefits.map((benefit, idx) => (
              <div key={idx} style={{
                background: 'linear-gradient(to bottom right, rgba(67, 56, 202, 0.4), rgba(15, 23, 42, 0.4))',
                backdropFilter: 'blur(4px)',
                border: '1px solid rgba(79, 70, 229, 0.3)',
                borderRadius: '16px',
                padding: '2rem',
                transition: 'all 0.3s'
              }}>
                <div style={{color: '#60a5fa', marginBottom: '1rem'}}>
                  {benefit.icon}
                </div>
                <h3 style={{fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '0.75rem'}}>{benefit.title}</h3>
                <p style={{color: '#bfdbfe', lineHeight: 1.75}}>{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div id="contact" style={{padding: '5rem 1rem'}}>
        <div style={{
          maxWidth: '896px',
          margin: '0 auto',
          textAlign: 'center',
          background: 'linear-gradient(to right, #4f46e5, #1e40af)',
          borderRadius: '24px',
          padding: '3rem',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          border: '1px solid rgba(59, 130, 246, 0.3)'
        }}>
          <h2 style={{fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 'bold', marginBottom: '1.5rem'}}>
            Ready to Secure Your Digital Identity?
          </h2>
          <p style={{fontSize: '1.25rem', color: '#dbeafe', marginBottom: '2rem'}}>
            Join thousands of users who trust IDsec.live for their blockchain identity management.
          </p>
          <button style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            background: 'white',
            color: '#312e81',
            padding: '1rem 2.5rem',
            borderRadius: '8px',
            fontWeight: 'bold',
            fontSize: '1.125rem',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
            border: 'none',
            cursor: 'pointer'
          }}>
            Contact Us
            <ArrowRight style={{width: '1.25rem', height: '1.25rem'}} />
          </button>
        </div>
      </div>

      {/* Footer */}
      <footer style={{background: '#020617', padding: '3rem 1rem', borderTop: '1px solid rgba(79, 70, 229, 0.3)'}}>
        <div style={{maxWidth: '1280px', margin: '0 auto'}}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '2rem',
            marginBottom: '2rem'
          }}>
            <div>
              <div style={{display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '1rem'}}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: 'linear-gradient(to bottom right, #3b82f6, #4f46e5)',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transform: 'rotate(45deg)'
                }}>
                  <Shield style={{width: '24px', height: '24px', color: 'white', transform: 'rotate(-45deg)'}} />
                </div>
                <span style={{fontSize: '1.25rem', fontWeight: 'bold'}}>IDsec.live</span>
              </div>
              <p style={{color: '#93c5fd', fontSize: '0.875rem'}}>Blockchain-powered identity management for the modern world.</p>
            </div>
            
            <div>
              <h4 style={{fontWeight: 'bold', marginBottom: '1rem'}}>Product</h4>
              <ul style={{listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem', color: '#93c5fd', fontSize: '0.875rem'}}>
                <li><a href="#features" style={{color: '#93c5fd', textDecoration: 'none'}}>Features</a></li>
                <li><a href="#security" style={{color: '#93c5fd', textDecoration: 'none'}}>Security</a></li>
                <li><a href="#technology" style={{color: '#93c5fd', textDecoration: 'none'}}>Technology</a></li>
              </ul>
            </div>
            
            <div>
              <h4 style={{fontWeight: 'bold', marginBottom: '1rem'}}>Company</h4>
              <ul style={{listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem', color: '#93c5fd', fontSize: '0.875rem'}}>
                <li><a href="#" style={{color: '#93c5fd', textDecoration: 'none'}}>About Us</a></li>
                <li><a href="#" style={{color: '#93c5fd', textDecoration: 'none'}}>Careers</a></li>
                <li><a href="#contact" style={{color: '#93c5fd', textDecoration: 'none'}}>Contact</a></li>
              </ul>
            </div>
            
            <div>
              <h4 style={{fontWeight: 'bold', marginBottom: '1rem'}}>Legal</h4>
              <ul style={{listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem', color: '#93c5fd', fontSize: '0.875rem'}}>
                <li><a href="#" style={{color: '#93c5fd', textDecoration: 'none'}}>Privacy Policy</a></li>
                <li><a href="#" style={{color: '#93c5fd', textDecoration: 'none'}}>Terms of Service</a></li>
                <li><a href="#" style={{color: '#93c5fd', textDecoration: 'none'}}>Security</a></li>
              </ul>
            </div>
          </div>
          
          <div style={{borderTop: '1px solid rgba(79, 70, 229, 0.3)', paddingTop: '2rem', textAlign: 'center', color: '#93c5fd', fontSize: '0.875rem'}}>
            <p>&copy; 2025 IDsec.live. All rights reserved. Powered by blockchain technology.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
